import React, { FC, useCallback, useEffect, useState } from 'react'

import { IconChevronDown, IconChevronRight } from '@avantstay/backoffice-vectors'

import { getNewUuid, useIsMobile } from '../../utils'
import { Button } from '../Button'
import { FloatingContainer } from '../FloatingContainer'
import { ActionLinkItem } from './ActionLinkItem'
import * as S from './ActionLinks.styles'
import { ActionItemProps, ActionLinksProps } from './ActionLinks.types'

const ActionLinks: FC<ActionLinksProps> = ({
  title,
  actions,
  actionsTitle,
  extraActions,
  nestedMenu,
  onActionSuccess,
}) => {
  const [activeNestedMenuIndex, setActiveNestedMenuIndex] = useState<number | undefined>(undefined)
  const [isOpen, setIsOpen] = useState(false)

  const isMobile = useIsMobile()

  const toggleIfIsOpened = useCallback(() => {
    setIsOpen(false)
    setActiveNestedMenuIndex(undefined)
  }, [])

  useEffect(
    () => (onActionSuccess ? toggleIfIsOpened() : undefined),
    [toggleIfIsOpened, onActionSuccess],
  )

  const handleCloseMenu = () => {
    setIsOpen(false)
    setActiveNestedMenuIndex(undefined)
  }

  const handleOpenMenu = () => {
    setIsOpen(!isOpen)
  }

  const randomId = getNewUuid()

  const renderActionsLinks = (actionLinks: Array<ActionItemProps>) => {
    return actionLinks.map(link => (
      <ActionLinkItem
        key={`${link.label}-${randomId}`}
        actionPending={link.actionPending}
        action={link.action}
        label={link.label}
      />
    ))
  }

  const renderNestedMenu = nestedMenu?.map((item, key) => {
    const handleOpenNestedMenu = () => {
      setActiveNestedMenuIndex(key)
    }
    return (
      <React.Fragment key={item.title}>
        {activeNestedMenuIndex === key && (
          <S.ActionLinkNestedContainer>
            {renderActionsLinks(item.nestedActions)}
            {item.extraNestedActions}
          </S.ActionLinkNestedContainer>
        )}

        <S.ActionLink
          onMouseOver={handleOpenNestedMenu}
          onFocus={handleOpenNestedMenu}
          active={activeNestedMenuIndex === key}
        >
          <S.ActionLinkContent>
            {item.title} <IconChevronRight />
          </S.ActionLinkContent>
        </S.ActionLink>
      </React.Fragment>
    )
  })

  const trigger = title ? (
    <Button.Flat onClick={handleOpenMenu}>
      {title}{' '}
      <S.IconWrapper>
        <IconChevronDown />
      </S.IconWrapper>
    </Button.Flat>
  ) : (
    <S.IconMoreStyled onClick={handleOpenMenu} />
  )

  const handleCloseNestedMenus = () => {
    setActiveNestedMenuIndex(undefined)
  }

  return (
    <S.ActionContainer>
      {isMobile ? <S.IconMoreStyled onClick={handleOpenMenu} /> : trigger}

      <FloatingContainer open={isOpen} placement="right" onClickOut={handleCloseMenu}>
        <S.ActionLinkContainer>
          {nestedMenu && renderNestedMenu}

          <div onMouseOver={handleCloseNestedMenus} onFocus={handleCloseNestedMenus}>
            {actionsTitle ? <S.ActionsTitle>{actionsTitle}</S.ActionsTitle> : null}
            {actions && renderActionsLinks(actions)}
          </div>

          {extraActions}
        </S.ActionLinkContainer>
      </FloatingContainer>
    </S.ActionContainer>
  )
}

export { ActionLinks }
